import { useEffect, useState, useCallback } from "react";
import api, { formatErr, loadPublicBaseUrl, publicLink } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Package, Truck, Paperclip, Trash, X } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import DeliveryUploadBox from "./DeliveryUploadBox";
import { useBackClose } from "@/hooks/use-back-close";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function PendingOrders() {
  // Receipt link ke liye server ka IP pehle se le aao (WhatsApp button pe turant chahiye)
  useEffect(() => { loadPublicBaseUrl(); }, []);
  const { user } = useAuth();
  // Sales Reps (role="user") cannot mark orders delivered or delete records —
  // those actions are Admin/Owner only. This flag hides the Deliver/Delete
  // controls (and the row-select checkboxes that only exist to bulk-delete)
  // for Sales Reps, everywhere on this page.
  const canManage = user?.role === "owner" || user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [confirm, setConfirm] = useState(null); // row pending "mark delivered" confirmation
  // True once WhatsApp has been opened for the current `confirm` row and
  // we're waiting for the user to say the message was actually sent —
  // delivery is only marked on the server after that second click.
  const [awaitingWhatsapp, setAwaitingWhatsapp] = useState(false);
  const [receiptRow, setReceiptRow] = useState(null); // row with the receipt-upload dialog open
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const pageSize = 10;

  // Phone/browser back button closes whichever dialog is open instead of
  // navigating the page away (which used to jump out of the app).
  useBackClose(!!confirm, () => setConfirm(null));
  useBackClose(!!receiptRow, () => setReceiptRow(null));
  useBackClose(!!deleteConfirm, () => setDeleteConfirm(null));
  useBackClose(bulkDeleteOpen, () => setBulkDeleteOpen(false));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sales", {
        params: { status: "pending", search, date, page, page_size: pageSize, sort_by: "date" },
      });
      setRows(data.items);
      setTotal(data.total);
      // Drop any selected invoices that no longer exist in the freshly loaded list.
      setSelected((prev) => {
        const invoiceNos = new Set(data.items.map((r) => r.invoice_no));
        return new Set(Array.from(prev).filter((inv) => invoiceNos.has(inv)));
      });
    } catch (e) { toast.error(formatErr(e)); }
  }, [search, date, page]);

  useEffect(() => { load(); }, [load]);

  // Clear selection whenever the visible list changes shape (filter, date, or page change).
  useEffect(() => { setSelected(new Set()); }, [search, date, page]);

  // Every time the deliver dialog opens for a (new) row, or closes, start
  // back at step 1 ("Open WhatsApp") rather than carrying over a stale
  // "waiting to confirm" state from a previous attempt.
  useEffect(() => { setAwaitingWhatsapp(false); }, [confirm]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.invoice_no));
  const someSelected = rows.some((r) => selected.has(r.invoice_no));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.invoice_no));
      return next;
    });
  };

  const toggleOne = (invoice_no) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(invoice_no)) next.delete(invoice_no);
      else next.add(invoice_no);
      return next;
    });
  };

  // Step 1: open WhatsApp with the delivery message pre-filled. Does NOT mark
  // the order delivered — that only happens in step 2, after the user has
  // actually sent the message and comes back to confirm it.
  const openWhatsapp = (e) => {
    // Stop AlertDialogAction's built-in "click closes the dialog" behavior —
    // this button only opens WhatsApp and switches to step 2, it should not
    // close the dialog yet.
    e.preventDefault();
    if (!confirm) return;

    // window.open must run synchronously inside the click handler (before any
    // await), otherwise browsers block it as a popup.
    const digits = (confirm.party_mobile || "").replace(/\D/g, "");
    const withCountryCode = digits.length === 10 ? `91${digits}` : digits; // assumes India (+91) for 10-digit numbers
    let message = `Dear ${confirm.party_name}, your material against Bill No - ${confirm.invoice_no} dated ${confirm.date} has been dispatched.`;
    if (confirm.receipt_file_path) {
      const receiptUrl = publicLink(confirm.receipt_file_path);
      message += `\nReceipt: ${receiptUrl}`;
    }
    window.open(`https://wa.me/${withCountryCode}?text=${encodeURIComponent(message)}`, "_blank");
    setAwaitingWhatsapp(true);
  };

  // Step 2: user has sent the WhatsApp message and confirms it — only now is
  // the order actually marked delivered on the server.
  const doDeliver = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post(`/sales/${confirm.id}/deliver`);
      toast.success(`Marked as delivered — WhatsApp opened for ${confirm.party_name}`);
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/sales/${encodeURIComponent(deleteConfirm.id)}`);
      toast.success(`Invoice ${deleteConfirm.invoice_no} deleted`);
      setDeleteConfirm(null);
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setDeleting(false); }
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const invoice_numbers = Array.from(selected);
      const { data } = await api.post("/sales/bulk-delete", { invoice_numbers });
      toast.success(`Deleted ${data.deleted} record${data.deleted === 1 ? "" : "s"}`);
      setBulkDeleteOpen(false);
      setSelected(new Set());
      if (rows.length === invoice_numbers.length && page > 1) setPage((p) => p - 1);
      else load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBulkDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="pending-page">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Pending Orders</h1>
        <p className="text-sm text-muted-foreground mt-1">Orders not yet delivered — mark delivered or attach a receipt.</p>
      </div>

      <div className="brut-card">
        <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search Invoice No, Party, Mobile"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-signal/40"
              data-testid="pending-search-input"
            />
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => { setDate(e.target.value); setPage(1); }}
            data-testid="pending-date-filter"
            className="h-10 px-3 rounded-md border border-border bg-background text-sm"
          />
          {(search || date) && (
            <button
              onClick={() => { setSearch(""); setDate(""); setPage(1); }}
              className="h-10 px-3 rounded-md border border-border text-sm hover:bg-muted"
              data-testid="pending-clear-filters-button"
            >
              Clear
            </button>
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-xs uppercase tracking-[0.05em] text-muted-foreground">
                {canManage && (
                  <th className="text-left px-4 py-3 font-semibold w-10">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      disabled={rows.length === 0}
                      aria-label="Select all rows on this page"
                      data-testid="pending-select-all-checkbox"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-semibold">Voucher Type</th>
                <th className="text-left px-4 py-3 font-semibold">Invoice No</th>
                <th className="text-left px-4 py-3 font-semibold">Salesman</th>
                <th className="text-left px-4 py-3 font-semibold">Transporter</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Party</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-right px-4 py-3 font-semibold">Invoice Amount</th>
                <th className="text-center px-4 py-3 font-semibold">Receipt</th>
                {canManage && <th className="text-right px-4 py-3 font-semibold">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={canManage ? 11 : 9} className="text-center py-16 text-muted-foreground">
                  <Package size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
                  No pending orders {(search || date) ? "match your filters" : "right now"}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t border-border hover:bg-muted/40 ${selected.has(r.invoice_no) ? "bg-signal/5" : ""}`}
                  data-testid={`pending-row-${r.invoice_no}`}
                >
                  {canManage && (
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selected.has(r.invoice_no)}
                        onCheckedChange={() => toggleOne(r.invoice_no)}
                        aria-label={`Select invoice ${r.invoice_no}`}
                        data-testid={`pending-row-checkbox-${r.invoice_no}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium">{r.voucher_type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.invoice_no}</td>
                  <td className="px-4 py-3">{r.salesman || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3">{r.transporter || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.date}</td>
                  <td className="px-4 py-3">{r.party_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.party_mobile}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(r.invoice_amount)}</td>
                  <td className="px-4 py-3 text-center">
                    {r.receipt_file_path ? (
                      <button
                        onClick={() => setReceiptRow(r)}
                        data-testid={`pending-receipt-btn-${r.invoice_no}`}
                        className="inline-flex items-center gap-1 text-xs font-bold text-signal hover:underline"
                      >
                        <Paperclip size={14} /> View / Replace
                      </button>
                    ) : (
                      <button
                        onClick={() => setReceiptRow(r)}
                        data-testid={`pending-receipt-btn-${r.invoice_no}`}
                        className="signal-btn h-8 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5"
                      >
                        <Paperclip size={14} weight="bold" /> Upload
                      </button>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setConfirm(r)}
                          data-testid={`pending-deliver-btn-${r.invoice_no}`}
                          className="signal-btn h-8 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5"
                        >
                          <Truck size={14} weight="bold" /> Deliver
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(r)}
                          data-testid={`pending-delete-btn-${r.invoice_no}`}
                          aria-label={`Delete invoice ${r.invoice_no}`}
                          title="Delete"
                          className="h-8 w-8 rounded-md inline-flex items-center justify-center border border-signal/40 bg-signal/10 text-signal hover:bg-signal/20 transition-colors"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-border">
          {rows.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Package size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
              No pending orders {(search || date) ? "match your filters" : "right now"}
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`pending-card-${r.invoice_no}`}
              className={`p-4 space-y-3 ${selected.has(r.invoice_no) ? "bg-signal/5" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  {canManage && (
                    <div className="pt-0.5">
                      <Checkbox
                        checked={selected.has(r.invoice_no)}
                        onCheckedChange={() => toggleOne(r.invoice_no)}
                        aria-label={`Select invoice ${r.invoice_no}`}
                        data-testid={`pending-row-checkbox-mobile-${r.invoice_no}`}
                      />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-muted-foreground">{r.invoice_no}</div>
                    <div className="font-semibold leading-snug break-words line-clamp-2">{r.party_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r.voucher_type} • {r.date}</div>
                  </div>
                </div>
                <div className="font-mono font-semibold text-right whitespace-nowrap shrink-0">{inr(r.invoice_amount)}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Mobile</div>
                  <div className="font-mono text-[13px]">{r.party_mobile}</div>
                </div>
                {r.salesman && (
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Salesman</div>
                    <div className="text-[13px] leading-snug break-words">{r.salesman}</div>
                  </div>
                )}
                {r.transporter && (
                  <div className="col-span-2 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Transporter</div>
                    <div className="text-[13px] leading-snug break-words">🚚 {r.transporter}</div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canManage && (
                  <button
                    onClick={() => setConfirm(r)}
                    data-testid={`pending-deliver-btn-mobile-${r.invoice_no}`}
                    className="signal-btn h-9 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5"
                  >
                    <Truck size={14} weight="bold" /> Deliver
                  </button>
                )}
                <button
                  onClick={() => setReceiptRow(r)}
                  data-testid={`pending-receipt-btn-mobile-${r.invoice_no}`}
                  className={r.receipt_file_path
                    ? "h-9 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5 border border-border hover:bg-muted"
                    : "signal-btn h-9 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5"}
                >
                  <Paperclip size={14} weight="bold" /> {r.receipt_file_path ? "View" : "Upload"}
                </button>
                {canManage && (
                  <button
                    onClick={() => setDeleteConfirm(r)}
                    data-testid={`pending-delete-btn-mobile-${r.invoice_no}`}
                    className="h-9 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5 border border-signal/40 bg-signal/10 text-signal"
                  >
                    <Trash size={14} weight="bold" /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} pending</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="h-8 px-3 rounded-md border border-border disabled:opacity-40 hover:bg-muted">Prev</button>
            <span className="font-mono text-xs">Page {page} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
              className="h-8 px-3 rounded-md border border-border disabled:opacity-40 hover:bg-muted">Next</button>
          </div>
        </div>
      </div>

      {/* Floating bulk action bar — appears once 1+ rows are selected */}
      {canManage && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 rounded-md border border-border bg-card shadow-lg max-w-[calc(100vw-2rem)] flex-wrap justify-center"
          data-testid="pending-bulk-action-bar"
        >
          <span className="text-sm font-medium">
            {selected.size} item{selected.size === 1 ? "" : "s"} selected
          </span>
          <button
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="pending-bulk-delete-button"
            className="h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
          >
            <Trash size={15} weight="bold" /> Delete Selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            data-testid="pending-bulk-clear-button"
            aria-label="Clear selection"
            className="h-9 w-9 rounded-md border border-border inline-flex items-center justify-center hover:bg-muted"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Deliver via WhatsApp confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="pending-deliver-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {awaitingWhatsapp ? "Confirm Message Sent" : "Confirm Delivery"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {awaitingWhatsapp ? (
                <>
                  WhatsApp has been opened for <strong>{confirm?.party_name}</strong>. Once you've
                  actually sent the message, click below to mark Invoice {confirm?.invoice_no} as delivered.
                </>
              ) : (
                <>
                  Are you sure this material has been delivered to <strong>{confirm?.party_name}</strong> (Invoice {confirm?.invoice_no})?
                  WhatsApp will open so you can notify {confirm?.party_mobile}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pending-deliver-cancel-button">Cancel</AlertDialogCancel>
            {awaitingWhatsapp ? (
              <AlertDialogAction onClick={doDeliver} disabled={busy} data-testid="pending-deliver-confirm-button" className="signal-btn">
                {busy ? "Saving..." : "I've Sent It — Mark Delivered"}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction onClick={openWhatsapp} data-testid="pending-deliver-open-whatsapp-button" className="signal-btn">
                Open WhatsApp
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent data-testid="pending-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete invoice <strong>{deleteConfirm?.invoice_no}</strong> ({deleteConfirm?.party_name}).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pending-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              data-testid="pending-delete-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <AlertDialogContent data-testid="pending-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Records</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selected.size}</strong> selected record{selected.size === 1 ? "" : "s"}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pending-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={bulkDeleting}
              data-testid="pending-bulk-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt upload / delivery note dialog */}
      <Dialog open={!!receiptRow} onOpenChange={(o) => !o && setReceiptRow(null)}>
        <DialogContent data-testid="pending-receipt-upload-dialog">
          <DialogHeader>
            <DialogTitle>Delivery Receipt — {receiptRow?.invoice_no}</DialogTitle>
            <DialogDescription>
              Upload the delivery receipt / proof for <strong>{receiptRow?.party_name}</strong> and add a note.
            </DialogDescription>
          </DialogHeader>
          {receiptRow && (
            <DeliveryUploadBox
              saleId={receiptRow.id}
              invoiceNo={receiptRow.invoice_no}
              deliveryStatus={receiptRow.delivery_status}
              receiptFilePath={receiptRow.receipt_file_path}
              onConfirmed={(updatedSale) => {
                // Dialog band nahi karte — upload ke baad isi dialog mein
                // receipt ka image preview dikhe. List background mein refresh.
                if (updatedSale) setReceiptRow(updatedSale);
                load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
